import { useEffect, useRef, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { apiClient, extractErrorMessage, getStoredToken } from '@/lib/api';
import { Loader2, Camera, Check, X as XIcon, ImageDown, Search } from 'lucide-react';
import { tryGetLocation } from '@/lib/construction-offline';

const API_BASE = (import.meta.env.VITE_API_URL as string) || 'https://api.jasrhub.com';
const MAX_CAPTION = 500;

interface Villa { id: string; villaName: string; model: string; blockNumber: number | null; }
interface StageDef { id: string; code: string; nameEn: string; isLeaf: boolean; }

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  projectId: string;
  /** When set, locks villa selection (used from VillaDetailPage). */
  lockedVillaId?: string;
}

export function AddPerformancePhotoSheet({ open, onClose, onCreated, projectId, lockedVillaId }: Props) {
  const [caption, setCaption] = useState('');
  const [villaId, setVillaId] = useState<string>(lockedVillaId ?? '');
  const [stageDefId, setStageDefId] = useState<string>('');
  const [villaSearch, setVillaSearch] = useState('');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [villas, setVillas] = useState<Villa[]>([]);
  const [stages, setStages] = useState<StageDef[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setVillaId(lockedVillaId ?? '');
    apiClient.get<Villa[]>(`/api/v1/construction/projects/${projectId}/villas`).then((r) => setVillas(r.data));
    apiClient.get<StageDef[]>(`/api/v1/construction/projects/${projectId}/stage-definitions`).then((r) => setStages(r.data));
  }, [open, projectId, lockedVillaId]);

  useEffect(() => {
    if (!photoPreview) return;
    return () => URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  const reset = () => {
    setCaption('');
    setVillaId(lockedVillaId ?? '');
    setStageDefId('');
    setVillaSearch('');
    setPhotoFile(null);
    setPhotoPreview((p) => { if (p) URL.revokeObjectURL(p); return null; });
    setError(null);
  };

  function pickPhoto(f: File) {
    setPhotoFile(f);
    setPhotoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(f); });
  }
  function clearPhoto() {
    setPhotoFile(null);
    setPhotoPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (fileRef.current) fileRef.current.value = '';
  }

  const handleSubmit = async () => {
    setError(null);
    const captionTrim = caption.trim();
    if (captionTrim.length === 0) { setError('Caption is required'); return; }
    if (captionTrim.length > MAX_CAPTION) { setError(`Caption too long (max ${MAX_CAPTION})`); return; }
    if (!photoFile) { setError('Please take a photo'); return; }

    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', photoFile);
      fd.append('caption', captionTrim);
      if (villaId) fd.append('villaId', villaId);
      if (stageDefId) fd.append('stageDefId', stageDefId);
      const gps = await tryGetLocation();
      if (gps) {
        fd.append('lat', String(gps.lat));
        fd.append('lng', String(gps.lng));
      }
      const jwt = getStoredToken();
      const res = await fetch(
        `${API_BASE}/api/v1/construction-photos/projects/${projectId}/performance`,
        { method: 'POST', headers: jwt ? { Authorization: `Bearer ${jwt}` } : {}, body: fd },
      );
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Upload failed: ${txt || res.statusText}`);
      }
      reset();
      onCreated();
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const filteredVillas = villas.filter((v) => {
    if (!villaSearch.trim()) return true;
    const q = villaSearch.toLowerCase();
    return v.villaName.toLowerCase().includes(q) || (v.model ?? '').toLowerCase().includes(q);
  });
  const leafStages = stages.filter((s) => s.isLeaf);

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ImageDown className="h-5 w-5" />
            Add work photo
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="perf-cap" className="flex items-center justify-between">
              <span>Caption</span>
              <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">{caption.length}/{MAX_CAPTION}</span>
            </Label>
            <Input
              id="perf-cap"
              autoFocus
              value={caption}
              onChange={(e) => setCaption(e.target.value.slice(0, MAX_CAPTION))}
              placeholder="e.g. V-316 raft pour complete"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="perf-villa" className="flex items-center gap-2">
              Villa
              <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
            </Label>
            {lockedVillaId ? (
              <div className="text-sm py-2.5 px-3 rounded-md border border-input bg-muted/30">
                {villas.find((v) => v.id === lockedVillaId)?.villaName ?? 'This villa'}
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search V-114, V-205…"
                    value={villaSearch}
                    onChange={(e) => setVillaSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <select
                  id="perf-villa"
                  value={villaId}
                  onChange={(e) => { setVillaId(e.target.value); setStageDefId(''); }}
                  size={Math.min(6, Math.max(3, filteredVillas.length))}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">— No villa —</option>
                  {filteredVillas.map((v) => (
                    <option key={v.id} value={v.id}>{v.villaName} · {v.model}{v.blockNumber != null ? ` · B${v.blockNumber}` : ''}</option>
                  ))}
                </select>
              </>
            )}
          </div>

          {villaId && leafStages.length > 0 && (
            <div className="space-y-1.5">
              <Label htmlFor="perf-stage" className="flex items-center gap-2">
                Stage
                <span className="text-[10px] normal-case tracking-normal font-normal text-muted-foreground">(optional)</span>
              </Label>
              <select
                id="perf-stage"
                value={stageDefId}
                onChange={(e) => setStageDefId(e.target.value)}
                className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">— No stage —</option>
                {leafStages.map((s) => (
                  <option key={s.id} value={s.id}>{s.code} — {s.nameEn}</option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Photo</Label>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) pickPhoto(f);
                e.target.value = '';
              }}
            />
            {photoPreview ? (
              <div className="flex items-center gap-3 rounded-md border border-border p-2">
                <img src={photoPreview} alt="" className="h-16 w-16 rounded object-cover bg-muted" />
                <div className="flex-1 text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                  <Check className="h-3.5 w-3.5" /> Ready to upload
                </div>
                <Button type="button" variant="ghost" size="icon" onClick={clearPhoto} aria-label="Remove photo">
                  <XIcon className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full gap-2 border-dashed h-20"
                onClick={() => fileRef.current?.click()}
              >
                <Camera className="h-5 w-5" />
                Take photo
              </Button>
            )}
          </div>

          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm p-3">
              {error}
            </div>
          )}
        </div>

        <SheetFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} disabled={busy}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={busy} className="gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
